/**
 * 「K-MRS 약사 접근코드」 영역 — 목록·발급·폐기·원문 재열람(복사·보기).
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — 화면 설계 A, Phase 3(개정 2026-10-16), D7a
 *
 * 「외부 연동」 목록 화면(`index.tsx`)이 파트너 표 아래에 이 영역을 **항상 한 번** 렌더한다(특정 파트너
 * 상세가 아니다 — 2026-10-16 오스카 정정). 별도 메뉴·라우트는 없다. 목록은 `includeRevoked=true`로 한 번
 * 받아 `revokedAt`으로 활성/폐기를 나눈다(N = 폐기 건수). 발급 직후 원문 창은 선례 `SecretRevealModal`을
 * 재사용한다(원문 요소에 `amp-mask`가 이미 있다). 폐기 확인은 선례 `showConfirm`을 재사용한다.
 *
 * 원문 재열람(D7a): 각 행 [복사](reveal→클립보드)·[보기](행 안에 원문 펼침, 다시 누르면 접고 상태에서
 * 지움). `rawAvailable=false`면 두 버튼 대신 「원문 없음 · 다시 발급 필요」. 폐기된 코드도 열람 가능하다.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import { ConfirmManager } from "components/organisms/CommonUI";
import { formatDateTime } from "utils/partnerAdmin";
import type { AdminAccessCode, IssueAccessCodeBody } from "api/kmrsAdminService";
import { useKmrsAccessCodes, useKmrsAccessCodeMutations } from "hooks/kmrsAdmin/useKmrsAdmin";
import { showSecretReveal } from "../SecretRevealModal";
import { showConfirm } from "../confirmDialog";
import {
  ButtonRow,
  EmptyText,
  EnvBadgeBox,
  Muted,
  Notice,
  RawBox,
  Section,
  SectionHeader,
  SectionTitle,
  StatusText,
  Table,
  Td,
  Th,
  Tr,
} from "../style";
import { CODE_REVEAL_NOTICE, actorText, labelText, scopeText } from "./kmrsText";
import { copyRevealedCode } from "./revealCopy";
import IssueCodeModal from "./IssueCodeModal";

/** reveal(원문 재열람) — 호출부가 훅의 mutateAsync를 넘긴다. 저장·로그 금지. */
type RevealFn = (id: string) => Promise<{ code: string }>;

/** 발급 창은 선례대로 배경 클릭·닫기(X) 없이 연다. */
const FORM_OPTIONS = { showBackdrop: true, backdropClose: false, showCloseButton: false } as const;

const CodeRow: React.FC<{
  code: AdminAccessCode;
  reveal: RevealFn;
  onRevoke?: (code: AdminAccessCode) => void;
}> = ({ code, reveal, onRevoke }) => {
  const revoked = !!code.revokedAt;
  // reveal 호출 중(복사·보기)에는 그 행의 두 버튼을 막는다. shownRaw는 [보기]로 펼친 원문 — 접으면 지운다.
  const [busy, setBusy] = useState(false);
  const [shownRaw, setShownRaw] = useState<string | null>(null);

  // [복사] — reveal 원문을 클립보드에만 넣는다(화면에 렌더하지 않음, #125 선례 방식).
  const copy = () => {
    setBusy(true);
    void copyRevealedCode(() => reveal(code.id), { onSettled: () => setBusy(false) });
  };

  // [보기] — 펼쳐 있으면 접고 원문을 상태에서 지운다. 접혀 있으면 reveal해서 행 안에 펼친다.
  const toggleView = async () => {
    if (shownRaw !== null) {
      setShownRaw(null);
      return;
    }
    setBusy(true);
    try {
      const { code: raw } = await reveal(code.id);
      setShownRaw(raw);
    } catch {
      // 사유(409 원문 없음 등)는 훅(onError)이 토스트로 보여준다. 펼치지 않는다.
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tr $muted={revoked} data-testid={`kmrs-code-row-${code.id}`}>
        <Td>{labelText(code.label)}</Td>
        <Td>{scopeText(code)}</Td>
        <Td>
          {formatDateTime(code.createdAt)}
          <Muted> · {actorText(code.createdBy)}</Muted>
        </Td>
        <Td data-testid={`kmrs-code-status-${code.id}`}>
          {revoked ? (
            <span>
              폐기됨 · {formatDateTime(code.revokedAt)}
              {code.revokedBy && ` · ${actorText(code.revokedBy)}`}
            </span>
          ) : (
            <StatusText $on>사용 중</StatusText>
          )}
        </Td>
        <Td>
          {code.rawAvailable ? (
            <ButtonRow>
              <Button
                type="Text"
                size="XSmall"
                variant="Outlined"
                color="Primary"
                disabled={busy}
                onClick={copy}
                data-testid={`kmrs-code-copy-${code.id}`}
              >
                복사
              </Button>
              <Button
                type="Text"
                size="XSmall"
                variant="Ghost"
                color="Secondary"
                disabled={busy}
                onClick={() => void toggleView()}
                data-testid={`kmrs-code-view-${code.id}`}
              >
                {shownRaw !== null ? "접기" : "보기"}
              </Button>
            </ButtonRow>
          ) : (
            <Muted data-testid={`kmrs-code-raw-none-${code.id}`}>원문 없음 · 다시 발급 필요</Muted>
          )}
        </Td>
        <Td>
          {!revoked && onRevoke && (
            <Button
              type="Text"
              size="XSmall"
              variant="Outlined"
              color="Negative"
              onClick={() => onRevoke(code)}
              data-testid={`kmrs-code-revoke-${code.id}`}
            >
              폐기
            </Button>
          )}
        </Td>
      </Tr>
      {shownRaw !== null && (
        <tr data-testid={`kmrs-code-raw-row-${code.id}`}>
          <Td colSpan={6}>
            {/* amp-mask: Amplitude 세션 녹화에서 원문을 가린다(D2 완화책) */}
            <RawBox className="amp-mask" data-testid={`kmrs-code-raw-${code.id}`} aria-label="원문">
              {shownRaw}
            </RawBox>
          </Td>
        </tr>
      )}
    </>
  );
};

const CodeTable: React.FC<{
  codes: AdminAccessCode[];
  testId: string;
  reveal: RevealFn;
  onRevoke?: (code: AdminAccessCode) => void;
}> = ({ codes, testId, reveal, onRevoke }) => (
  <Table data-testid={testId}>
    <thead>
      <tr>
        <Th>이름표</Th>
        <Th>열람 범위</Th>
        <Th>발급 시각 · 발급자</Th>
        <Th>상태</Th>
        <Th>원문</Th>
        <Th />
      </tr>
    </thead>
    <tbody>
      {codes.map((c) => (
        <CodeRow key={c.id} code={c} reveal={reveal} onRevoke={onRevoke} />
      ))}
    </tbody>
  </Table>
);

const KmrsAccessCodeSection: React.FC<{ isRel: boolean }> = ({ isRel }) => {
  const codes = useKmrsAccessCodes();
  const mutations = useKmrsAccessCodeMutations();
  const [showRevoked, setShowRevoked] = useState(false);

  // 원문 재열람(D7a) — 각 행이 [복사]·[보기]에서 쓴다. 실패 사유는 훅(onError)이 토스트로 보여준다.
  const reveal: RevealFn = (id) => mutations.reveal.mutateAsync(id);

  // 발급 성공 → 원문 창(선례 재사용). 실패하면 던져서 발급 창이 폼을 유지한다(사유는 훅 토스트).
  const handleIssue = async (body: IssueAccessCodeBody) => {
    const result = await mutations.issue.mutateAsync(body);
    showSecretReveal({
      title: "접근코드 원문",
      raw: result.code,
      provision: null,
      notice: CODE_REVEAL_NOTICE,
    });
  };

  const openIssue = () => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: <IssueCodeModal isRel={isRel} onIssue={handleIssue} onCancel={() => ConfirmManager.hide()} />,
    });
  };

  const revoke = (code: AdminAccessCode) => {
    showConfirm({
      title: "접근코드 폐기",
      lines: [
        `'${labelText(code.label)}'(${scopeText(code)}, ${formatDateTime(code.createdAt)} 발급) 코드를 폐기할까요?`,
        "되돌릴 수 없어요. 이 코드로 들어와 있는 사람도 바로 나가게 됩니다.",
      ],
      isRel,
      confirmLabel: "폐기",
      negative: true,
      onConfirm: () => mutations.revoke.mutateAsync(code.id),
    });
  };

  const items = codes.data?.items ?? [];
  const active = items.filter((c) => !c.revokedAt);
  const revokedCodes = items.filter((c) => !!c.revokedAt);

  const header = (
    <SectionHeader>
      <SectionTitle>K-MRS 약사 접근코드</SectionTitle>
      <ButtonRow>
        {isRel && (
          <EnvBadgeBox $rel data-testid="kmrs-rel-badge">
            운영 환경입니다
          </EnvBadgeBox>
        )}
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          onClick={() => codes.refetch()}
          data-testid="kmrs-refresh"
        >
          새로 고침
        </Button>
        <Button
          type="Text"
          size="Small"
          variant="Contained"
          color="Primary"
          onClick={openIssue}
          data-testid="kmrs-code-issue"
        >
          코드 발급
        </Button>
      </ButtonRow>
    </SectionHeader>
  );

  let body: React.ReactNode;
  if (codes.isLoading) {
    body = <EmptyText>불러오는 중입니다.</EmptyText>;
  } else if (codes.isError) {
    body = (
      <Notice $tone="warn" data-testid="kmrs-list-error">
        목록을 불러오지 못했어요. {codes.error?.message}{" "}
        <Button type="Text" size="XSmall" variant="Ghost" color="Secondary" onClick={() => codes.refetch()}>
          다시 불러오기
        </Button>
      </Notice>
    );
  } else {
    body = (
      <>
        {active.length > 0 ? (
          <CodeTable codes={active} testId="kmrs-code-table" reveal={reveal} onRevoke={revoke} />
        ) : (
          <EmptyText data-testid="kmrs-empty">발급된 코드가 없어요</EmptyText>
        )}
        {revokedCodes.length > 0 && (
          <div>
            <Button
              type="Text"
              size="XSmall"
              variant="Ghost"
              color="Secondary"
              onClick={() => setShowRevoked((v) => !v)}
              data-testid="kmrs-show-revoked"
            >
              {showRevoked ? "폐기된 코드 접기" : `폐기된 코드 보기 (${revokedCodes.length})`}
            </Button>
            {showRevoked && <CodeTable codes={revokedCodes} testId="kmrs-revoked-table" reveal={reveal} />}
          </div>
        )}
      </>
    );
  }

  return (
    <Section data-testid="kmrs-access-code-section">
      {header}
      {body}
    </Section>
  );
};

export default KmrsAccessCodeSection;
